/**
 * The dock: side panels around a main workspace, each of which can be
 * minimised to a slim strip, maximised over the whole workspace, or restored.
 *
 * One rule keeps it legible: only one panel is maximised at a time, and a
 * panel that is not in use stays minimised until something needs it (a node
 * is selected, a run finishes). The choice is remembered per screen.
 *
 *   const dock = useDock('workflow.editor', { palette: 'open', inspector: 'min', runs: 'min' })
 *   <Dock top={<header/>} left={dock.state.palette} right={dock.state.inspector} bottom={dock.state.runs}>
 *     <DockPanel side="left"  title="Nodes" state={dock.state.palette} onState={(s) => dock.set('palette', s)}>…</DockPanel>
 *     <DockMain>…</DockMain>
 *     <DockPanel side="right" …>…</DockPanel>
 *   </Dock>
 */

import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { Icon } from '@/shared/components/icons'
import './dock.css'

export type DockState = 'open' | 'min' | 'max'
export type DockSide = 'left' | 'right' | 'bottom'

// --- state ------------------------------------------------------------------

export function useDock<K extends string>(storageKey: string, initial: Record<K, DockState>) {
  const key = `dock.${storageKey}`
  const [state, setState] = useState<Record<K, DockState>>(() => {
    try {
      const raw = localStorage.getItem(key)
      if (raw) {
        const saved = JSON.parse(raw) as Partial<Record<K, DockState>>
        // Never resume maximised: a screen that opens covered by one panel is
        // disorienting. Everything else is exactly as it was left.
        const merged = { ...initial }
        for (const k of Object.keys(initial) as K[]) {
          const v = saved[k]
          if (v === 'open' || v === 'min') merged[k] = v as DockState
        }
        return merged
      }
    } catch {
      // Storage is a nicety.
    }
    return initial
  })

  const set = useCallback(
    (panel: K, next: DockState) => {
      setState((prev) => {
        const out = { ...prev }
        if (next === 'max') {
          for (const k of Object.keys(out) as K[]) if (k !== panel && out[k] === 'max') out[k] = 'open'
        }
        out[panel] = next
        try {
          localStorage.setItem(key, JSON.stringify(out))
        } catch {
          // ignore
        }
        return out
      })
    },
    [key],
  )

  /** Bring a panel into view if it is tucked away — used when something selects into it. */
  const reveal = useCallback(
    (panel: K) => {
      setState((prev) => (prev[panel] === 'min' ? { ...prev, [panel]: 'open' } : prev))
    },
    [],
  )

  const maximized = (Object.keys(state) as K[]).find((k) => state[k] === 'max') ?? null

  return useMemo(() => ({ state, set, reveal, maximized }), [state, set, reveal, maximized])
}

// --- layout -----------------------------------------------------------------

const STRIP = 40

interface DockProps {
  top?: ReactNode
  left?: DockState
  right?: DockState
  bottom?: DockState
  /** Open widths/heights in px. Minimised is always the strip size. */
  sizes?: { left?: number; right?: number; bottom?: number }
  className?: string
  children: ReactNode
  style?: CSSProperties
}

function slot(state: DockState | undefined, open: number): string {
  if (!state) return '0px'
  if (state === 'min') return `${STRIP}px`
  if (state === 'max') return '0px'
  return `${open}px`
}

export function Dock({ top, left, right, bottom, sizes, className, children, style }: DockProps) {
  const vars = {
    '--dock-l': slot(left, sizes?.left ?? 264),
    '--dock-r': slot(right, sizes?.right ?? 340),
    '--dock-b': slot(bottom, sizes?.bottom ?? 280),
    ...style,
  } as CSSProperties
  const covered = left === 'max' || right === 'max' || bottom === 'max'
  return (
    <div className={`dock${className ? ` ${className}` : ''}`} style={vars} data-covered={covered || undefined}>
      {top && <div className="dock-top">{top}</div>}
      {children}
    </div>
  )
}

export function DockMain({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={`dock-main${className ? ` ${className}` : ''}`}>{children}</div>
}

// --- one panel --------------------------------------------------------------

interface PanelProps {
  side: DockSide
  title: string
  icon?: ReactNode
  state: DockState
  onState: (s: DockState) => void
  /** Extra controls in the header, before the window buttons. */
  actions?: ReactNode
  /** Shown on the minimised strip beside the title (a count, a status pill). */
  summary?: ReactNode
  className?: string
  bodyClassName?: string
  children: ReactNode
}

export function DockPanel({ side, title, icon, state, onState, actions, summary, className, bodyClassName, children }: PanelProps) {
  const isMax = state === 'max'
  const isMin = state === 'min'

  if (isMin) {
    return (
      <button
        type="button"
        className={`dock-strip dock-strip-${side}${className ? ` ${className}` : ''}`}
        onClick={() => onState('open')}
        title={`Show ${title.toLowerCase()}`}
        aria-label={`Show ${title.toLowerCase()}`}
      >
        <span className="dock-strip-icon">{icon ?? <Icon.expand />}</span>
        <span className="dock-strip-title">{title}</span>
        {summary && <span className="dock-strip-summary">{summary}</span>}
      </button>
    )
  }

  return (
    <section
      className={`dock-panel dock-panel-${side}${className ? ` ${className}` : ''}`}
      data-state={state}
      aria-label={title}
    >
      <header className="dock-head">
        {icon && <span className="dock-head-icon">{icon}</span>}
        <h3 className="dock-title">{title}</h3>
        {actions && <div className="dock-actions">{actions}</div>}
        <div className="dock-window">
          <button type="button" className="dock-btn" onClick={() => onState('min')} title="Minimise" aria-label={`Minimise ${title.toLowerCase()}`}>
            <Icon.minimize />
          </button>
          {isMax ? (
            <button type="button" className="dock-btn" onClick={() => onState('open')} title="Restore" aria-label={`Restore ${title.toLowerCase()}`}>
              <Icon.restore />
            </button>
          ) : (
            <button type="button" className="dock-btn" onClick={() => onState('max')} title="Maximise" aria-label={`Maximise ${title.toLowerCase()}`}>
              <Icon.maximize />
            </button>
          )}
        </div>
      </header>
      <div className={`dock-body${bodyClassName ? ` ${bodyClassName}` : ''}`}>{children}</div>
    </section>
  )
}
